import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCadProjects } from '../../services/cadService';
import { ownedCadProjectsForLists } from '../utils/cadCollectionsForLists';
import { applyCadListPinSync, syncCadListPinRow } from '../utils/cadOwnerMapOverlay';

/**
 * Índice de listas CAD (`GET /getcadprojects`). Una fila por lista:
 * `lat`/`lng`, `parcel_count`, `brand`. Sirve los pines del mapa país.
 *
 * @param {{ enabled?: boolean, reloadKey?: number, ownerUserId?: string|null }} [options]
 */
export function useCadProjectsIndex({ enabled = true, reloadKey = 0, ownerUserId = null } = {}) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const pinSyncRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setProjects([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const spaceOwnerId = ownerUserId ? String(ownerUserId).trim() : '';
        const result = await getCadProjects({
          signal: controller.signal,
          ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
        });
        if (controller.signal.aborted) return;
        const synced = applyCadListPinSync(
          result.ok ? result.projects : [],
          pinSyncRef.current,
        );
        pinSyncRef.current = synced.pending;
        setProjects(synced.projects);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('CAD mapa país: GET /getcadprojects falló', error);
        setProjects([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [enabled, reloadKey, ownerUserId]);

  const owned = useMemo(
    () => ownedCadProjectsForLists(projects, { spaceMirror: Boolean(ownerUserId) }),
    [projects, ownerUserId],
  );

  const dropProject = useCallback((project) => {
    const projectId = project?.id != null && project.id !== '' ? String(project.id) : '';
    const projectSlug = project?.slug != null && String(project.slug).trim()
      ? String(project.slug).trim()
      : '';
    if (!projectId && !projectSlug) return;
    setProjects((prev) => prev.filter((row) => {
      if (projectId && String(row?.id || '') === projectId) return false;
      if (projectSlug && String(row?.slug || '') === projectSlug) return false;
      return true;
    }));
  }, []);

  const upsertProject = useCallback((project) => {
    const projectId = project?.id != null && project.id !== '' ? String(project.id) : '';
    const projectSlug = project?.slug != null && String(project.slug).trim()
      ? String(project.slug).trim()
      : '';
    if (!projectId && !projectSlug) return;
    pinSyncRef.current = project;
    setProjects((prev) => prev.map((row) => {
      const sameId = projectId && String(row?.id || '') === projectId;
      const sameSlug = projectSlug && String(row?.slug || '') === projectSlug;
      if (!sameId && !sameSlug) return row;
      return syncCadListPinRow(row, project);
    }));
  }, []);

  return { loading, projects: owned, dropProject, upsertProject };
}
